import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import SignaturePad from '../common/SignaturePad.jsx'
import bodyPartsImg from '../img/bodyparts.jpg'

const DEFAULT_ASSESSMENT_SIGNATURE_URL = `${process.env.PUBLIC_URL || ''}/default-signature.png`

// ─── Option lists ──────────────────────────────────────────────────────────────

const TEXAS_OPTIONS = [
  { value: 'A0', label: 'A0 - Pre- or post-ulcerative lesion completely epithelialized' },
  { value: 'A1', label: 'A1 - Superficial wound not involving tendon' },
  { value: 'A2', label: 'A2 - Wound penetrating to tendon or capsule' },
  { value: 'A3', label: 'A3 - Wound penetrating to bone or joint' },
  { value: 'B0', label: 'B0 - Infection (low grade)' },
  { value: 'B1', label: 'B1 - Infection (moderate grade)' },
  { value: 'B2', label: 'B2 - Infection (medium - to high grade)' },
  { value: 'B3', label: 'B3 - Infection (severe grade)' },
  { value: 'C0', label: 'C0 - Ischemia (low grade)' },
  { value: 'C1', label: 'C1 - Ischemia (moderate grade)' },
  { value: 'C2', label: 'C2 - Ischemia (medium to high grade)' },
  { value: 'C3', label: 'C3 - Ischemia (severe grade)' },
  { value: 'D0', label: 'D0 - Infection and Ischemia (low grade)' },
  { value: 'D1', label: 'D1 - Infection and Ischemia (moderate grade)' },
  { value: 'D2', label: 'D2 - Infection and Ischemia (medium to high grade)' },
  { value: 'D3', label: 'D3 - Infection and Ischemia (severe grade)' },
]

const PRESSURE_STAGE_OPTIONS = [
  { value: '1', label: 'Stage 1 - Non-Blanchable Erythema' },
  { value: '2', label: 'Stage 2 - Partial-Thickness Skin Loss' },
  { value: '3', label: 'Stage 3 - Full-Thickness Skin Loss' },
  { value: '4', label: 'Stage 4 - Full-Thickness Tissue Loss' },
  { value: '5', label: 'Unstageable - Obscured Full-Thickness Loss Base' },
  { value: '6', label: 'DTPI - Deep Tissue Pressure Injury' },
]

const CEAP_OPTIONS = [
  { value: '1', label: 'C0 - no visible or palpable signs of venous disease' },
  { value: '2', label: 'C1 - Telangiectasias or reticular veins' },
  { value: '3', label: 'C2 - Varicose veins' },
  { value: '4', label: 'C3 - Oedema' },
  { value: '5', label: 'C4a - Pigmentation and/or Eczema' },
  { value: '6', label: 'C4b - Lipodermatosclerosis and/or Atrophie blanche' },
  { value: '7', label: 'C5 - Healed Venous Ulcer' },
  { value: '8', label: 'C6 - Active Venous Ulcer' },
]

const EXUDATE_AMOUNT_OPTIONS = [
  { value: '', label: '' },
  { value: '1', label: 'Low' },
  { value: '2', label: 'Moderate' },
  { value: '3', label: 'High' },
]

const NERDS_OPTIONS = [
  { key: 'neon', label: 'Non-Healing' },
  { key: 'effervescent', label: 'Exudate' },
  { key: 'rich', label: 'Red Friable Granulation' },
  { key: 'drainage', label: 'Debris' },
  { key: 'scintillating', label: 'Smell' },
]

const STONES_OPTIONS = [
  { key: 'size', label: 'Size is bigger' },
  { key: 'temperature', label: 'Temperature is increased' },
  { key: 'os', label: 'Os (Probe to bone)' },
  { key: 'newareas', label: 'New or Satellite areas of breakdown' },
  { key: 'exudate', label: 'Exudate' },
  { key: 'erythema', label: 'Erythema / edema' },
  { key: 'smell', label: 'Smell' },
]

const TREATMENT_GOALS = [
  { key: 'wound_closure', label: 'Wound closure' },
  { key: 'debride', label: 'Debride necrotic tissue' },
  { key: 'bacteria', label: 'Reduce Bacterial load' },
  { key: 'infection', label: 'Treat Infection' },
  { key: 'granulation', label: 'Encourage granulation' },
  { key: 'reduce_size', label: 'Reduced wound size and severity' },
  { key: 'moisture', label: 'Moisture balance' },
  { key: 'oedema', label: 'Reduce Oedema' },
]

const PAIN_FREQUENCY_OPTIONS = [
  { value: 'atDressing', label: 'At dressing stage' },
  { value: 'onMovement', label: 'On Movement' },
  { value: 'continuous', label: 'Continuous' },
  { value: 'other', label: 'Other' },
]

// Toggle these to tune pagination for generated PDFs without changing utility code.
const PDF_PAGE_BREAKS = {
  beforeSection6: false,
  beforeSection7: false,
  beforeSection8: false,
  beforeExudate: false,
}

// ─── Section header ────────────────────────────────────────────────────────────

function SectionHeader({ number, title }) {
  return (
    <Box sx={{ bgcolor: '#414148', px: 2, py: 1, borderRadius: 1, mb: 2, width: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#d8d8d8' }}>
        {number}. {title}
      </Typography>
    </Box>
  )
}

function PdfPageBreak({ enabled, id }) {
  if (!enabled) {
    return null
  }

  return <Box data-pdf-page-break-before={id} sx={{ height: 0 }} />
}

// html2canvas cannot reliably wrap <textarea> contents, so render a plain
// wrapped text block instead of the MUI TextField while generating the PDF.
function MultilineTextField({ pdfMode, label, value, minHeight = 90, ...textFieldProps }) {
  if (pdfMode) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Box
          sx={{
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: 1,
            p: '16.5px 14px',
            minHeight,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            bgcolor: '#fff',
          }}
        >
          {value || ''}
        </Box>
      </Box>
    )
  }

  return (
    <TextField {...textFieldProps} multiline label={label} value={value || ''} />
  )
}

// ─── Body diagram canvas ───────────────────────────────────────────────────────

const getCanvasPoint = (canvas, clientX, clientY) => {
  const rect = canvas.getBoundingClientRect()
  return {
    x: ((clientX - rect.left) / rect.width) * canvas.width,
    y: ((clientY - rect.top) / rect.height) * canvas.height,
  }
}

const BODY_ANNOTATION_EXPORT_MAX_WIDTH = 900
const BODY_ANNOTATION_MAX_BYTES = 850000
const BODY_ANNOTATION_JPEG_QUALITIES = [0.72, 0.62, 0.52, 0.45]
const BODY_ANNOTATION_MAX_DPR = 2

const getByteLength = (value) => {
  if (!value) return 0
  return new TextEncoder().encode(value).length
}

const exportBodyAnnotation = (canvas) => {
  const sourceWidth = Number(canvas?.width) || 0
  const sourceHeight = Number(canvas?.height) || 0
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return ''
  }

  const exportCanvas = document.createElement('canvas')
  const exportCtx = exportCanvas.getContext('2d')
  if (!exportCtx) {
    return canvas.toDataURL('image/jpeg', BODY_ANNOTATION_JPEG_QUALITIES[0])
  }

  const cssWidth = Number(canvas.clientWidth) || sourceWidth
  const scale = Math.min(1, BODY_ANNOTATION_EXPORT_MAX_WIDTH / cssWidth)
  const targetWidth = Math.max(1, Math.floor(sourceWidth * scale))
  const targetHeight = Math.max(1, Math.floor(sourceHeight * scale))

  exportCanvas.width = targetWidth
  exportCanvas.height = targetHeight

  // JPEG has no alpha channel; fill with white to avoid transparent regions turning black.
  exportCtx.fillStyle = '#ffffff'
  exportCtx.fillRect(0, 0, targetWidth, targetHeight)
  exportCtx.drawImage(canvas, 0, 0, targetWidth, targetHeight)

  let fallbackDataUrl = ''
  for (const quality of BODY_ANNOTATION_JPEG_QUALITIES) {
    const dataUrl = exportCanvas.toDataURL('image/jpeg', quality)
    fallbackDataUrl = dataUrl
    if (getByteLength(dataUrl) <= BODY_ANNOTATION_MAX_BYTES) {
      return dataUrl
    }
  }

  return fallbackDataUrl
}

function BodyDiagram({ value, onChange, height = 360 }) {
  const canvasRef = useRef(null)
  const bgImgRef = useRef(null)
  const savedDataRef = useRef(value || '')
  const isDrawingRef = useRef(false)
  const [ready, setReady] = useState(false)

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    if (savedDataRef.current) {
      const img = new Image()
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      img.src = savedDataRef.current
    } else if (bgImgRef.current) {
      ctx.drawImage(bgImgRef.current, 0, 0, canvas.width, canvas.height)
    }
  }, [])

  const configureCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), BODY_ANNOTATION_MAX_DPR)
    const parentWidth = canvas.parentElement?.clientWidth || 500
    canvas.style.width = `${parentWidth}px`
    canvas.style.height = `${height}px`
    canvas.width = Math.floor(parentWidth * dpr)
    canvas.height = Math.floor(height * dpr)
    const ctx = canvas.getContext('2d')
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#cc0000'
    ctx.lineWidth = 3 * dpr
    redrawCanvas()
  }, [height, redrawCanvas])

  useEffect(() => {
    const img = new Image()
    img.onload = () => {
      bgImgRef.current = img
      setReady(true)
    }
    img.src = bodyPartsImg
  }, [])

  useEffect(() => {
    if (!ready) return
    configureCanvas()
    window.addEventListener('resize', configureCanvas)
    return () => window.removeEventListener('resize', configureCanvas)
  }, [ready, configureCanvas])

  useEffect(() => {
    savedDataRef.current = value || ''
    redrawCanvas()
  }, [value, redrawCanvas])

  const handlePointerDown = (e) => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.setPointerCapture(e.pointerId)
    const ctx = canvas.getContext('2d')
    const dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), BODY_ANNOTATION_MAX_DPR)
    ctx.strokeStyle = '#cc0000'
    ctx.lineWidth = 3 * dpr
    const p = getCanvasPoint(canvas, e.clientX, e.clientY)
    ctx.beginPath()
    ctx.arc(p.x, p.y, 4 * dpr, 0, Math.PI * 2)
    ctx.fillStyle = '#cc0000'
    ctx.fill()
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    isDrawingRef.current = true
  }

  const handlePointerMove = (e) => {
    if (!isDrawingRef.current) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const p = getCanvasPoint(canvas, e.clientX, e.clientY)
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
  }

  const finishDrawing = () => {
    if (!isDrawingRef.current) return
    isDrawingRef.current = false
    const canvas = canvasRef.current
    if (!canvas) return
    const dataUrl = exportBodyAnnotation(canvas)
    savedDataRef.current = dataUrl
    onChange(dataUrl)
  }

  const handleClear = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    savedDataRef.current = ''
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    if (bgImgRef.current) ctx.drawImage(bgImgRef.current, 0, 0, canvas.width, canvas.height)
    onChange('')
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Mark wound location(s) on the body diagram below
      </Typography>
      <canvas
        ref={canvasRef}
        style={{
          border: '1px solid #ccc',
          borderRadius: 8,
          cursor: 'crosshair',
          touchAction: 'none',
          display: 'block',
          width: '100%',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishDrawing}
        onPointerLeave={finishDrawing}
        onPointerCancel={finishDrawing}
      />
      <Button variant="outlined" size="small" onClick={handleClear} sx={{ mt: 1 }}>
        Clear marks
      </Button>
    </Box>
  )
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function AssessmentEditor({
  classes,
  assessment = {},
  patient = {},
  groupDetails = {},
  onChange,
  pdfMode = false,
}) {
  // ── Helpers ──

  const toFlagObject = (value) => {
    if (Array.isArray(value)) return value.reduce((acc, item) => ({ ...acc, [item]: true }), {})
    if (value && typeof value === 'object') return value
    if (typeof value === 'string')
      return value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .reduce((acc, item) => ({ ...acc, [item]: true }), {})
    return {}
  }

  const handleGroupToggle = (field, optionKey, checked) => {
    const next = { ...toFlagObject(assessment[field]), [optionKey]: checked }
    onChange({ target: { name: field, value: next } })
  }

  const isGroupChecked = (field, optionKey) => !!toFlagObject(assessment[field])[optionKey]

  const handleGoalChange = (goalKey, column, checked) => {
    const current = assessment.treatmentGoals || {}
    const cur = current[goalKey] || { shortTerm: '', longTerm: '' }
    let next
    if (column === 'shortTerm') {
      next = { shortTerm: checked ? 'X' : '', longTerm: checked ? '' : cur.longTerm }
    } else {
      next = { shortTerm: checked ? '' : cur.shortTerm, longTerm: checked ? 'X' : '' }
    }
    onChange({ target: { name: 'treatmentGoals', value: { ...current, [goalKey]: next } } })
  }

  const getGoalValue = (goalKey, column) => {
    const g = (assessment.treatmentGoals || {})[goalKey] || {}
    return g[column] || ''
  }

  const handleBodyAnnotation = (dataUrl) => {
    onChange({ target: { name: 'bodyAnnotation', value: dataUrl } })
  }

  const handleSignatureChange = (dataUrl) => {
    onChange({ target: { name: 'assessmentSignatureDataUrl', value: dataUrl } })
  }

  const handleSignedAt = (isoString) => {
    const dateVal = isoString ? new Date(isoString).toISOString().slice(0, 10) : ''
    onChange({ target: { name: 'assessmentSignatureDate', value: dateVal } })
  }

  const [defaultSignatureDataUrl, setDefaultSignatureDataUrl] = useState('')
  const [hasAppliedDefaultSignature, setHasAppliedDefaultSignature] = useState(false)

  useEffect(() => {
    let cancelled = false

    const loadDefaultSignature = async () => {
      try {
        const response = await fetch(DEFAULT_ASSESSMENT_SIGNATURE_URL)
        if (!response.ok) return
        const blob = await response.blob()
        const reader = new FileReader()
        reader.onloadend = () => {
          if (cancelled) return
          if (typeof reader.result === 'string') {
            setDefaultSignatureDataUrl(reader.result)
          }
        }
        reader.readAsDataURL(blob)
      } catch {
        // Keep manual signing available if the default asset cannot be loaded.
      }
    }

    loadDefaultSignature()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (pdfMode || hasAppliedDefaultSignature) return

    if (assessment.assessmentSignatureDataUrl) {
      setHasAppliedDefaultSignature(true)
      return
    }

    if (!defaultSignatureDataUrl) return

    onChange({ target: { name: 'assessmentSignatureDataUrl', value: defaultSignatureDataUrl } })
    if (!assessment.assessmentSignatureDate) {
      const today = new Date().toISOString().slice(0, 10)
      onChange({ target: { name: 'assessmentSignatureDate', value: today } })
    }
    setHasAppliedDefaultSignature(true)
  }, [
    assessment.assessmentSignatureDataUrl,
    assessment.assessmentSignatureDate,
    defaultSignatureDataUrl,
    hasAppliedDefaultSignature,
    onChange,
    pdfMode,
  ])

  const signaturePadValue =
    assessment.assessmentSignatureDataUrl ||
    (!hasAppliedDefaultSignature ? defaultSignatureDataUrl : '')

  // ── Derived state ──

  const hasWoundType = !!(
    assessment.postsurgical ||
    assessment.diabeticUlcer ||
    assessment.pressureUlcer ||
    assessment.venousUlcer ||
    assessment.trauma ||
    assessment.burns ||
    assessment.other
  )

  // ── Shared styles ──

  const subsectionStyle = {
    borderLeft: '3px solid #90a4ae',
    paddingLeft: 12,
    marginBottom: 14,
    marginTop: 6,
  }

  const subsectionTitleStyle = {
    fontWeight: 700,
    fontSize: '1.1rem',
    marginBottom: 6,
    color: '#2b3a42',
    backgroundColor: '#d5dde3',
    padding: '1px 10px',
    borderRadius: 6,
  }
const subsubsectionTitleStyle = {
    fontWeight: 700,
    fontSize: '1.0rem',
    marginBottom: 5,
    color: '#2b3a42',
    // backgroundColor: '#d5dde3',
    padding: '1px 35px',
    borderRadius: 6,
  }

  const sectionCardSx = {
    bgcolor: '#fbefed',
    borderRadius: 2,
    p: 2,
    mb: 2,
    breakInside: 'avoid',
    pageBreakInside: 'avoid',
  }

  // ══════════════════════════════════════════════════════════════════════════════
  return (
    <form className={classes?.form} noValidate style={{ padding: 16 }}>
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 1.5, bgcolor: '#650028b9', borderRadius: 2 }}>
        {pdfMode && (
          <Box data-pdf-section="0" sx={{ bgcolor: '#fff', borderRadius: 2, p: 2, mb: 2, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
              Application Form for Advance Wound Care 2026
            </Typography>
          </Box>
        )}

        {/* ── Section 1: Patient's details ───────────────────────────────────── */}
        <Box data-pdf-section="1" sx={sectionCardSx}>
          <SectionHeader number={1} title="Patient's details" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Title / Gender" value={patient.gender || ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Surname" value={patient.patientName || ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="First Name(s)" value={patient.firstName || ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="ID / Passport Number" value={patient.membershipNo || patient.SAID || ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Telephone (Home)" value={patient.principalHomeTel || ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Telephone (Work)" value={patient.principalWorkTel || ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Cell phone" value={patient.mobile || ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth variant="outlined" label="Email" value={patient.email || ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </Box>

        {/* ── Section 2: Practice details ────────────────────────────────────── */}
        <Box data-pdf-section="2" sx={sectionCardSx}>
          <SectionHeader number={2} title="Practice details" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              {/* <TextField fullWidth variant="outlined" label="Registered Nurse's practice number" value={groupDetails.hdetail3 || ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} /> */}
              <TextField fullWidth variant="outlined" label="Registered Nurse's practice number" value='0764809' InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Treating doctor's practice number" value={patient.referringDrNo || ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Out-patient facility's practice number" value={assessment.outpatientPracticeNo || ''} name="outpatientPracticeNo" onChange={onChange} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </Box>

        {/* ── Section 3: History ─────────────────────────────────────────────── */}
        <Box data-pdf-section="3" sx={sectionCardSx}>
          <SectionHeader number={3} title="History of previous advanced or conservative wound treatment" />
          <MultilineTextField
            pdfMode={pdfMode}
            fullWidth
            minRows={4}
            minHeight={110}
            variant="outlined"
            label="Treatment history"
            name="treatment"
            value={assessment.treatment || ''}
            onChange={onChange}
          />
        </Box>

        {/* ── Section 4: Wound location ──────────────────────────────────────── */}
        <Box data-pdf-section="4" sx={sectionCardSx}>
          <SectionHeader number={4} title="Wound location" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="ICD-10 codes" value={patient.ICD10Codes || ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" type="date" label="Date wound first occurred" name="woundStartDate" value={assessment.woundStartDate || ''} onChange={onChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" type="date" label="Treatment start date" name="treatmentStartDate" value={assessment.treatmentStartDate || ''} onChange={onChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <BodyDiagram value={assessment.bodyAnnotation || ''} onChange={handleBodyAnnotation} />
            </Grid>
          </Grid>
        </Box>

        {/* ── Section 5: Wound Description ──────────────────────────────────── */}
        <Box data-pdf-section="5" sx={sectionCardSx}>
          <SectionHeader number={5} title="Wound Description" />

          {/* 13 wound-type checkboxes, 4 columns per row */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.postsurgical} name="postsurgical" onChange={onChange} />} label="Surgical wounds" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.venousUlcer} name="venousUlcer" onChange={onChange} />} label="Venous ulcer" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.fistulae} name="fistulae" onChange={onChange} />} label="Entero-cutaneous fistulae" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.trauma} name="trauma" onChange={onChange} />} label="Trauma" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.diabeticUlcer} name="diabeticUlcer" onChange={onChange} />} label="Diabetic wounds" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.pressureUlcer} name="pressureUlcer" onChange={onChange} />} label="Pressure ulcer" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.pilonidalCyst} name="pilonidalCyst" onChange={onChange} />} label="Pilonidal cyst" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.skinCancer} name="skinCancer" onChange={onChange} />} label="Malignant neoplasms of skin" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.arterialUlcer} name="arterialUlcer" onChange={onChange} />} label="Arterial ulcer" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.burns} name="burns" onChange={onChange} />} label="Burns" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.gangren} name="gangren" onChange={onChange} />} label="Pyoderma gangrenosum" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.skinGrafting} name="skinGrafting" onChange={onChange} />} label="Skin grafting" /></Grid>
            <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.other} name="other" onChange={onChange} />} label="Other" /></Grid>
          </Grid>

          {/* Conditional wound-type sub-forms */}
          {(hasWoundType || pdfMode) && (
            <Box>
              {(assessment.postsurgical || pdfMode) && (
                <Box style={subsectionStyle}>
                  <Typography variant="subtitle1" style={subsectionTitleStyle}>Surgical Wounds</Typography>
                  <TextField fullWidth multiline minRows={3} maxRows={3} variant="outlined" label="Surgical Procedure" name="surgicalProc" value={assessment.surgicalProc || ''} onChange={onChange} />
                </Box>
              )}
              {(assessment.diabeticUlcer || pdfMode) && (
                <Box style={subsectionStyle}>
                  <Typography variant="subtitle1" style={subsectionTitleStyle}>Diabetic Ulcers</Typography>
                  <TextField select fullWidth variant="outlined" label="Texas University Classification" name="dstage" value={assessment.dstage || ''} onChange={onChange}>
                    {TEXAS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                </Box>
              )}
              {(assessment.pressureUlcer || pdfMode) && (
                <Box style={subsectionStyle}>
                  <Typography variant="subtitle1" style={subsectionTitleStyle}>Pressure Ulcers</Typography>
                  <TextField select fullWidth variant="outlined" label="Stage" name="pstage" value={assessment.pstage || ''} onChange={onChange}>
                    {PRESSURE_STAGE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                </Box>
              )}
              {(assessment.venousUlcer || pdfMode) && (
                <Box style={subsectionStyle}>
                  <Typography variant="subtitle1" style={subsectionTitleStyle}>Venous Ulcers</Typography>
                  <TextField select fullWidth variant="outlined" label="CEAP Classification" name="vstage" value={assessment.vstage || ''} onChange={onChange}>
                    {CEAP_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                </Box>
              )}
              {(assessment.trauma || pdfMode) && (
                <Box style={subsectionStyle}>
                  <Typography variant="subtitle1" style={subsectionTitleStyle}>Trauma</Typography>
                  <TextField fullWidth multiline minRows={3} maxRows={3} variant="outlined" label="Diagnostic Findings" name="traumaDesc" value={assessment.traumaDesc || ''} onChange={onChange} />
                </Box>
              )}
              {(assessment.burns || pdfMode) && (
                <Box style={subsectionStyle}>
                  <Typography variant="subtitle1" style={subsectionTitleStyle}>Burns</Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <FormControlLabel control={<Checkbox checked={!!assessment.burnDegree1} name="burnDegree1" onChange={onChange} />} label="First degree" />
                      <FormControlLabel control={<Checkbox checked={!!assessment.burnDegree2} name="burnDegree2" onChange={onChange} />} label="Second degree" />
                      <FormControlLabel control={<Checkbox checked={!!assessment.burnDegree3} name="burnDegree3" onChange={onChange} />} label="Third degree" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth variant="outlined" type="number" inputProps={{ min: 0, max: 100 }} label="Total Body Surface Area (%)" name="burnArea" value={assessment.burnArea || ''} onChange={onChange} />
                    </Grid>
                  </Grid>
                </Box>
              )}
              {(assessment.other || pdfMode) && (
                <Box style={subsectionStyle}>
                  <Typography variant="subtitle1" style={subsectionTitleStyle}>Other</Typography>
                  <MultilineTextField pdfMode={pdfMode} fullWidth minRows={3} maxRows={3} variant="outlined" label="Describe other wound type" name="otherDesc" value={assessment.otherDesc || ''} onChange={onChange} />
                </Box>
              )}
            </Box>
          )}

          {/* ── Healability ─────────────────────────────────────────────────── */}
          <Box style={subsectionStyle}>
            <Typography variant="subtitle1" style={subsectionTitleStyle}>Healability</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.healableWound} name="healableWound" onChange={onChange} />} label="Healable wound" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.maintenanceWound} name="maintenanceWound" onChange={onChange} />} label="Maintenance wound" /></Grid>
              <Grid item xs={12} md={6}><FormControlLabel control={<Checkbox checked={!!assessment.nonHealableWound} name="nonHealableWound" onChange={onChange} />} label="Non-healable / palliative wound" /></Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth variant="outlined" label="Other" name="healabilityOther" value={assessment.healabilityOther || ''} onChange={onChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth variant="outlined" label="Site of wound" name="siteOfWound" value={assessment.siteOfWound || ''} onChange={onChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth variant="outlined" label="Size of wound" name="sizeOfWound" value={assessment.sizeOfWound || ''} onChange={onChange} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth variant="outlined" label="Length (cm)" name="woundLength" value={assessment.woundLength || ''} onChange={onChange} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth variant="outlined" label="Width (cm)" name="woundWidth" value={assessment.woundWidth || ''} onChange={onChange} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth variant="outlined" label="Depth (cm)" name="woundDepth" value={assessment.woundDepth || ''} onChange={onChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth variant="outlined" label="Volume (cm³)" name="woundVolume" value={assessment.woundVolume || ''} onChange={onChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth variant="outlined" label="Undermining / Sinus" name="woundUndermining" value={assessment.woundUndermining || ''} onChange={onChange} />
              </Grid>
            </Grid>
          </Box>

          {/* ── Wound bed condition ─────────────────────────────────────────── */}
          <Box style={subsectionStyle}>
            <Typography variant="subtitle1" style={subsectionTitleStyle}>Wound bed condition (100%)</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.epithelialisation} name="epithelialisation" onChange={onChange} />} label="Epithelialisation" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.granulation} name="granulation" onChange={onChange} />} label="Granulation" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.slough} name="slough" onChange={onChange} />} label="Slough / Fibrin" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.blackNecrotic} name="blackNecrotic" onChange={onChange} />} label="Black Necrotic Tissue" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.overGranulating} name="overGranulating" onChange={onChange} />} label="Over Granulating" /></Grid>
              <Grid item xs={12} md={4}><FormControlLabel control={<Checkbox checked={!!assessment.fungating} name="fungating" onChange={onChange} />} label="Fungating / Malignant" /></Grid>
              <Grid item xs={12} md={5}><FormControlLabel control={<Checkbox checked={!!assessment.mixedTissue} name="mixedTissue" onChange={onChange} />} label="Mixed Tissue (Bone / Tendon / Ligament)" /></Grid>
            </Grid>
          </Box>

          {/* ── Infection ───────────────────────────────────────────────────── */}
          <Box style={subsectionStyle}>
            <Typography variant="subtitle1" style={subsectionTitleStyle}>Infection</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.colonised} name="colonised" onChange={onChange} />} label="Colonised" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.superficial} name="superficial" onChange={onChange} />} label="Superficial" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.biofilm} name="biofilm" onChange={onChange} />} label="Biofilm" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.inflammation} name="inflammation" onChange={onChange} />} label="Inflammation" /></Grid>
            </Grid>

            <Typography variant="subtitle2" style={subsubsectionTitleStyle} sx={{ mt: 2, mb: 1 }}>Superficial Indicators (NERDS)</Typography>
            <Grid container spacing={1}>
              {NERDS_OPTIONS.map((o) => (
                <Grid item xs={12} md={4} key={o.key}>
                  <FormControlLabel
                    control={<Checkbox checked={isGroupChecked('nerds', o.key)} onChange={(e) => handleGroupToggle('nerds', o.key, e.target.checked)} />}
                    label={o.label}
                  />
                </Grid>
              ))}
            </Grid>

            <Typography variant="subtitle2" style={subsubsectionTitleStyle} sx={{ mt: 1, mb: 0.5 }}>Deeper wound indicators (STONES)</Typography>
            <Grid container spacing={1}>
              {STONES_OPTIONS.map((o) => (
                <Grid item xs={12} md={4} key={o.key}>
                  <FormControlLabel
                    control={<Checkbox checked={isGroupChecked('stones', o.key)} onChange={(e) => handleGroupToggle('stones', o.key, e.target.checked)} />}
                    label={o.label}
                  />
                </Grid>
              ))}
            </Grid>

            <Typography variant="subtitle2" style={subsubsectionTitleStyle} sx={{ mt: 1.5, mb: 1 }}>Amount and colour of Exudate</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth variant="outlined" label="Colour" name="exudateColour" value={assessment.exudateColour || ''} onChange={onChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth variant="outlined" type="number" inputProps={{ min: 0 }} label="Amount (ml)" name="exudateAmount" value={assessment.exudateAmount || ''} onChange={onChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2">Serous (clear thin watery)</Typography>
                <TextField select fullWidth variant="outlined" label="Amount" name="serous" value={assessment.serous || ''} onChange={onChange}>
                  {EXUDATE_AMOUNT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2">Sanguineous (fresh blood)</Typography>
                <TextField select fullWidth variant="outlined" label="Amount" name="sanguineous" value={assessment.sanguineous || ''} onChange={onChange}>
                  {EXUDATE_AMOUNT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2">Serosanguineous (watery pale pink)</Typography>
                <TextField select fullWidth variant="outlined" label="Amount" name="serosang" value={assessment.serosang || ''} onChange={onChange}>
                  {EXUDATE_AMOUNT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2">Purulent (creamy, yellow, brown, green)</Typography>
                <TextField select fullWidth variant="outlined" label="Amount" name="purulent" value={assessment.purulent || ''} onChange={onChange}>
                  {EXUDATE_AMOUNT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
          </Box>

          {/* ── Conditions of surrounding skin ─────────────────────────────── */}
          <Box style={subsectionStyle}>
            <Typography variant="subtitle1" style={subsectionTitleStyle}>Conditions of surrounding skin</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.skinHealthy} name="skinHealthy" onChange={onChange} />} label="Healthy / Intact" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.skinDry} name="skinDry" onChange={onChange} />} label="Dry / Cracked / Eczema" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.skinDiscoloured} name="skinDiscoloured" onChange={onChange} />} label="Discoloured" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.skinErythema} name="skinErythema" onChange={onChange} />} label="Erythema / Fragile / Inflamed" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.skinMacerated} name="skinMacerated" onChange={onChange} />} label="Macerated" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.skinCallous} name="skinCallous" onChange={onChange} />} label="Callous / Senescent cells" /></Grid>
              <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={!!assessment.skinOedematous} name="skinOedematous" onChange={onChange} />} label="Oedematous" /></Grid>
            </Grid>
          </Box>

          {/* ── Wound healing phase ─────────────────────────────────────────── */}
          <Box style={subsectionStyle}>
            <Typography variant="subtitle1" style={subsectionTitleStyle}>Wound healing phase</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}><FormControlLabel control={<Checkbox checked={!!assessment.woundPhaseInflammatory} name="woundPhaseInflammatory" onChange={onChange} />} label="Inflammatory" /></Grid>
              <Grid item xs={12} md={4}><FormControlLabel control={<Checkbox checked={!!assessment.woundPhaseProliferation} name="woundPhaseProliferation" onChange={onChange} />} label="Proliferation" /></Grid>
              <Grid item xs={12} md={4}><FormControlLabel control={<Checkbox checked={!!assessment.woundPhaseMaturation} name="woundPhaseMaturation" onChange={onChange} />} label="Maturation" /></Grid>
            </Grid>
          </Box>

          {/* ── Pain Measurement ────────────────────────────────────────────── */}
          <Box style={subsectionStyle}>
            <Typography variant="subtitle1" style={subsectionTitleStyle}>Pain Measurement</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField fullWidth variant="outlined" type="number" inputProps={{ min: 0, max: 10, step: 1 }} label="Severity (0–10)" name="painLevel" value={assessment.painLevel || ''} onChange={onChange} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  0–1: no pain &nbsp;|&nbsp; 2–6: moderate &nbsp;|&nbsp; 7–10: severe
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField select fullWidth variant="outlined" label="Frequency" name="painFrequency" value={assessment.painFrequency || ''} onChange={onChange}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {PAIN_FREQUENCY_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* ── Section 6: Treatment Goals ────────────────────────────────────── */}
        <Box data-pdf-section="6" sx={sectionCardSx}>
          <SectionHeader number={6} title="Treatment Goals" />
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, width: '50%' }}>Treatment Goal</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '25%', textAlign: 'center' }}>Short term</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '25%', textAlign: 'center' }}>Long term</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {TREATMENT_GOALS.map((goal) => (
                <TableRow key={goal.key}>
                  <TableCell>{goal.label}</TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      size="small"
                      onClick={() => handleGoalChange(goal.key, 'shortTerm', getGoalValue(goal.key, 'shortTerm') !== 'X')}
                      inputProps={{
                        readOnly: true,
                        style: { textAlign: 'center', color: '#c62828', fontWeight: 700, cursor: 'pointer' },
                      }}
                      value={getGoalValue(goal.key, 'shortTerm')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleGoalChange(goal.key, 'shortTerm', getGoalValue(goal.key, 'shortTerm') !== 'X')
                        }
                      }}
                      sx={{ width: 80, mx: 'auto', display: 'block' }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      size="small"
                      onClick={() => handleGoalChange(goal.key, 'longTerm', getGoalValue(goal.key, 'longTerm') !== 'X')}
                      inputProps={{
                        readOnly: true,
                        style: { textAlign: 'center', color: '#c62828', fontWeight: 700, cursor: 'pointer' },
                      }}
                      value={getGoalValue(goal.key, 'longTerm')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleGoalChange(goal.key, 'longTerm', getGoalValue(goal.key, 'longTerm') !== 'X')
                        }
                      }}
                      sx={{ width: 80, mx: 'auto', display: 'block' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* ── Section 7: Treatment plan ─────────────────────────────────────── */}
        <Box data-pdf-section="7" sx={sectionCardSx}>
          <SectionHeader number={7} title="Treatment plan" />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth variant="outlined" label="Treatment dates" name="treatmentDates" value={assessment.treatmentDates || ''} onChange={onChange} />
            </Grid>
            <Grid item xs={12}>
              <MultilineTextField pdfMode={pdfMode} fullWidth minRows={3} minHeight={90} variant="outlined" label="Treatment plan / advanced wound care" name="treatmentPlan" value={assessment.treatmentPlan || ''} onChange={onChange} />
            </Grid>
          </Grid>
        </Box>

        {/* ── Section 8: Supporting documentation ──────────────────────────── */}
        {/* <PdfPageBreak enabled={PDF_PAGE_BREAKS.beforeSection8} id="section-8" /> */}
        <Box data-pdf-section="8" sx={sectionCardSx}>
          <SectionHeader number={8} title="Supporting documentation" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Checklist</Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={!!assessment.includePhoto} name="includePhoto" onChange={onChange} />}
                label="Include a dated, labelled colour photo with a wound ruler and patient details visible to show wound size"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={!!assessment.clinicalMotivation} name="clinicalMotivation" onChange={onChange} />}
                label="Signed and dated clinical motivation from the referring doctor"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={!!assessment.quotation} name="quotation" onChange={onChange} />}
                label="Quotation"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              {pdfMode ? (
                <div>
                  <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
                    Signature of Treating Healthcare Professional (sign with finger/stylus/mouse)
                  </Typography>
                  <div
                    style={{
                      border: '1px solid #c7c7c7',
                      borderRadius: 8,
                      backgroundColor: '#fff',
                      width: '100%',
                      maxWidth: 720,
                      height: 180,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {signaturePadValue ? (
                      <img
                        src={signaturePadValue}
                        alt="Assessment signature"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">No signature captured</Typography>
                    )}
                  </div>
                </div>
              ) : (
                <SignaturePad
                  value={signaturePadValue}
                  onChange={handleSignatureChange}
                  onSignedAtChange={handleSignedAt}
                  height={180}
                  subtitle="Signature of Treating Healthcare Professional (sign with finger/stylus/mouse)"
                />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                type="date"
                label="Signature date"
                name="assessmentSignatureDate"
                value={assessment.assessmentSignatureDate || ''}
                onChange={onChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>

      </Box>
    </form>
  )
}
